import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { PatchedTimeCardNote, TimeCardNote } from '../../../js/api';
import type { AssignmentNote } from '../../../js/api/models/AssignmentNote';
import type { ClearanceNote } from '../../../js/api/models/ClearanceNote';
import type { ConnectedFacilityNote } from '../../../js/api/models/ConnectedFacilityNote';
import type { NeedNote } from '../../../js/api/models/NeedNote';
import type { OpportunityNote } from '../../../js/api/models/OpportunityNote';
import type { PatchedAssignmentNote } from '../../../js/api/models/PatchedAssignmentNote';
import type { PatchedClearanceNote } from '../../../js/api/models/PatchedClearanceNote';
import type { PatchedConnectedFacilityNote } from '../../../js/api/models/PatchedConnectedFacilityNote';
import type { PatchedNeedNote } from '../../../js/api/models/PatchedNeedNote';
import type { PatchedOpportunityNote } from '../../../js/api/models/PatchedOpportunityNote';
import type { PatchedProviderNote } from '../../../js/api/models/PatchedProviderNote';
import type { ProviderNote } from '../../../js/api/models/ProviderNote';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { useIsMobile } from '../hooks/useIsMobile';
import type {
  ActivityNote,
  ActivityStatusHistory,
  ActivityHistory,
  ActivityEventHistory,
  UnifiedActivity,
} from '../utils/activity-types';
import { ActivityItem } from './activity-item';
import { Button } from './button';
import { Checkbox, CheckboxField } from './checkbox';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Field, Fieldset, Label } from './fieldset';
import { NoteChannelSelect, type NoteChannelConfig } from './note-channel-select';
import { Textarea } from './textarea';

export type NoteMutations = {
  updateNote: (
    noteId: string,
    data:
      | PatchedAssignmentNote
      | PatchedOpportunityNote
      | PatchedNeedNote
      | PatchedConnectedFacilityNote
      | PatchedClearanceNote
      | PatchedTimeCardNote
      | PatchedProviderNote
  ) => Promise<
    | AssignmentNote
    | OpportunityNote
    | NeedNote
    | ConnectedFacilityNote
    | ClearanceNote
    | TimeCardNote
    | ProviderNote
  >;
  deleteNote: (noteId: string) => Promise<void>;
  // Optional: provide a createNote callback to render the composer at the top
  // of ActivityLog. Omit it for read-only activity views. `channel` is only set
  // when ActivityLog was given a `noteChannel` config (staff channel picker).
  createNote?: (data: { comment: string; channel?: string }) => Promise<unknown>;
  notesQueryKey: readonly unknown[];
  // Optional: additional query keys to invalidate after note edits/deletes (e.g., status history)
  extraInvalidateQueryKeys?: readonly (readonly unknown[])[];
};

type ActivityLogProps = {
  notes: ActivityNote[] | undefined;
  statusHistory: ActivityStatusHistory[] | undefined;
  changeHistory: ActivityHistory[] | undefined;
  eventHistory?: ActivityEventHistory[] | undefined;
  noteMutations?: NoteMutations;
  enableCommentsFilter?: boolean;
  enableStatusChangesFilter?: boolean;
  enableUpdatesFilter?: boolean;
  enableEventsFilter?: boolean;
  // Optional placeholder for the composer textarea. Defaults to a generic
  // "Enter a comment..." prompt.
  composerPlaceholder?: string;
  // Optional: when provided, the composer and the note edit form show a
  // "Visible to" channel picker (CodeDefinitions-backed) and send `channel`
  // with the note. Omit it to keep the backend default channel (non-staff UX).
  noteChannel?: NoteChannelConfig;
};

// --- Composer ---
// Shared note creation form. Rendered at the top of ActivityLog when a
// createNote callback is provided via noteMutations. Owns its own form
// state, expand/collapse UX, and invalidation on success.

const composerSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
  channel: z.string().optional(),
});
type ComposerFormData = z.infer<typeof composerSchema>;

function NoteComposer({
  createNote,
  notesQueryKey,
  extraInvalidateQueryKeys,
  placeholder,
  noteChannel,
}: {
  createNote: NonNullable<NoteMutations['createNote']>;
  notesQueryKey: readonly unknown[];
  extraInvalidateQueryKeys?: readonly (readonly unknown[])[];
  placeholder?: string;
  noteChannel?: NoteChannelConfig;
}) {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [isExpanded, setIsExpanded] = useState(false);

  const form = useForm<ComposerFormData>({
    resolver: zodResolver(composerSchema),
    defaultValues: { comment: '', channel: noteChannel?.defaultValue },
  });

  const createMutation = useMutation({
    mutationFn: (data: ComposerFormData) =>
      createNote(
        noteChannel ? { comment: data.comment, channel: data.channel } : { comment: data.comment }
      ),
    onSuccess: async () => {
      toast.success('Note added successfully');
      // Keep the last-used channel: staff commonly post several notes to the same audience.
      form.reset({ comment: '', channel: form.getValues('channel') });
      setIsExpanded(false);
      await queryClient.invalidateQueries({ queryKey: notesQueryKey });
      if (extraInvalidateQueryKeys) {
        await Promise.all(
          extraInvalidateQueryKeys.map((key) => queryClient.invalidateQueries({ queryKey: key }))
        );
      }
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Error adding note' });
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit((data) => createMutation.mutate(data))(e);
      }}
      onFocus={() => setIsExpanded(true)}
      onBlur={(e) => {
        // Ignore focus moves between children (e.g. textarea → submit button).
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
        // Only collapse if the user hasn't typed anything.
        const currentComment = form.getValues('comment') ?? '';
        if (!currentComment.trim()) {
          setIsExpanded(false);
        }
      }}
      className="tw:mb-4"
    >
      <Fieldset>
        <div className={clsx('tw:grid tw:grid-cols-1 tw:gap-4', isMobile && 'tw:text-sm')}>
          <Field>
            <Textarea
              {...form.register('comment')}
              placeholder={placeholder ?? 'Enter a comment...'}
              rows={isExpanded ? 3 : 1}
              disabled={createMutation.isPending}
              data-testid="activity-log-comment-input"
            />
            {form.formState.errors.comment && (
              <FieldValidationErrorMessage message={form.formState.errors.comment.message} />
            )}
          </Field>
        </div>
        {isExpanded && (
          <div
            className={clsx(
              'tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:mt-2',
              noteChannel ? 'tw:justify-between' : 'tw:justify-end'
            )}
          >
            {noteChannel && (
              <Controller
                name="channel"
                control={form.control}
                render={({ field }) => (
                  <NoteChannelSelect
                    category={noteChannel.category}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={createMutation.isPending}
                    inputId="activity-log-channel-select"
                  />
                )}
              />
            )}
            <Button
              small={isMobile}
              type="submit"
              color="brandgreen0"
              disabled={createMutation.isPending}
              onClick={form.handleSubmit((data) => createMutation.mutate(data))}
              data-testid="activity-log-add-comment-button"
            >
              {createMutation.isPending ? 'Adding...' : 'Add Comment'}
            </Button>
          </div>
        )}
      </Fieldset>
    </form>
  );
}

/**
 * Compiles notes, status history, change history, and event history into a unified activity list
 */
function compileActivityList(
  notes: ActivityNote[] | undefined,
  statusHistory: ActivityStatusHistory[] | undefined,
  changeHistory: ActivityHistory[] | undefined,
  eventHistory: ActivityEventHistory[] | undefined
): UnifiedActivity[] {
  const activities: UnifiedActivity[] = [];

  if (notes && notes.length > 0) {
    notes.forEach((note) => {
      activities.push({
        id: note.id,
        activity_type: 'note',
        activity_datetime: note.note_at,
        activity_object: note,
        activity_icon: 'fas fa-comment-dots',
      });
    });
  }

  if (statusHistory && statusHistory.length > 0) {
    statusHistory.forEach((status) => {
      activities.push({
        id: status.id,
        activity_type: 'status',
        activity_datetime: status.change_at,
        activity_object: status,
        activity_icon: 'fas fa-sync-alt',
      });
    });
  }

  if (changeHistory && changeHistory.length > 0) {
    changeHistory.forEach((change) => {
      activities.push({
        id: change.id,
        activity_type: 'update',
        activity_datetime: change.change_at,
        activity_object: change,
        activity_icon: 'fas fa-user-pen',
      });
    });
  }

  if (eventHistory && eventHistory.length > 0) {
    eventHistory.forEach((event) => {
      activities.push({
        id: event.id,
        activity_type: 'event',
        activity_datetime: event.event_at,
        activity_object: event,
        activity_icon: 'fas fa-seal',
      });
    });
  }

  // Sort using raw ISO strings to preserve full microsecond precision from the
  // backend (new Date().getTime() truncates to milliseconds, losing ordering
  // for items that occur within the same millisecond).
  activities.sort((a, b) => b.activity_datetime.localeCompare(a.activity_datetime));

  return activities;
}

export function ActivityLog({
  notes,
  statusHistory,
  changeHistory,
  eventHistory,
  noteMutations,
  enableCommentsFilter = false,
  enableStatusChangesFilter = false,
  enableUpdatesFilter = false,
  enableEventsFilter = false,
  composerPlaceholder,
  noteChannel,
}: ActivityLogProps) {
  const [commentsVisibleInList, setCommentsVisibleInList] = useState(enableCommentsFilter);
  const [statusChangesVisibleInList, setStatusChangesVisibleInList] =
    useState(enableStatusChangesFilter);
  const [updatesVisibleInList, setUpdatesVisibleInList] = useState(enableUpdatesFilter);
  const [eventsVisibleInList, setEventsVisibleInList] = useState(enableEventsFilter);
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>('newest');

  const activityList = useMemo(() => {
    return compileActivityList(notes, statusHistory, changeHistory, eventHistory);
  }, [notes, statusHistory, changeHistory, eventHistory]);

  const filteredAndSortedActivityList = useMemo(() => {
    const isVisible = (type: UnifiedActivity['activity_type']) => {
      const visibilityMap = {
        note: enableCommentsFilter ? commentsVisibleInList : false,
        status: enableStatusChangesFilter ? statusChangesVisibleInList : false,
        update: enableUpdatesFilter ? updatesVisibleInList : false,
        event: enableEventsFilter ? eventsVisibleInList : false,
      };
      return visibilityMap[type];
    };
    let filtered = activityList.filter((activity) => isVisible(activity.activity_type));

    filtered = [...filtered].sort((a, b) => {
      return sortDirection === 'newest'
        ? b.activity_datetime.localeCompare(a.activity_datetime)
        : a.activity_datetime.localeCompare(b.activity_datetime);
    });

    return filtered;
  }, [
    activityList,
    enableCommentsFilter,
    enableStatusChangesFilter,
    enableUpdatesFilter,
    enableEventsFilter,
    commentsVisibleInList,
    statusChangesVisibleInList,
    updatesVisibleInList,
    eventsVisibleInList,
    sortDirection,
  ]);

  return (
    <>
      {/* Composer — rendered only when the caller provides a createNote */}
      {noteMutations?.createNote && (
        <NoteComposer
          createNote={noteMutations.createNote}
          notesQueryKey={noteMutations.notesQueryKey}
          extraInvalidateQueryKeys={noteMutations.extraInvalidateQueryKeys}
          placeholder={composerPlaceholder}
          noteChannel={noteChannel}
        />
      )}
      {filteredAndSortedActivityList.length > 0 ? (
        <>
          {/* Filter Bar */}
          <div
            className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-2 tw:mb-2 tw:mt-0 tw:p-2
          tw:border-y tw:border-zinc-300"
          >
            <div className="tw:flex tw:items-center tw:gap-2 tw:md:gap-3 tw:flex-wrap">
              {enableCommentsFilter && (
                <CheckboxField
                  className="tw:!gap-x-1 tw:md:!gap-x-2"
                  data-testid="activity-log-filter-comments"
                >
                  <Checkbox
                    checked={commentsVisibleInList}
                    onChange={(checked) => setCommentsVisibleInList(checked)}
                    color="brandgreen1"
                  />
                  <Label className="tw:!text-sm">Comments</Label>
                </CheckboxField>
              )}
              {enableStatusChangesFilter && (
                <CheckboxField
                  className="tw:!gap-x-1 tw:md:!gap-x-2"
                  data-testid="activity-log-filter-status"
                >
                  <Checkbox
                    checked={statusChangesVisibleInList}
                    onChange={(checked) => setStatusChangesVisibleInList(checked)}
                    color="brandgreen1"
                  />
                  <Label className="tw:!text-sm">Status</Label>
                </CheckboxField>
              )}
              {enableUpdatesFilter && (
                <CheckboxField
                  className="tw:!gap-x-1 tw:md:!gap-x-2"
                  data-testid="activity-log-filter-updates"
                >
                  <Checkbox
                    checked={updatesVisibleInList}
                    onChange={(checked) => setUpdatesVisibleInList(checked)}
                    color="brandgreen1"
                  />
                  <Label className="tw:!text-sm">Updates</Label>
                </CheckboxField>
              )}
              {enableEventsFilter && (
                <CheckboxField
                  className="tw:!gap-x-1 tw:md:!gap-x-2"
                  data-testid="activity-log-filter-events"
                >
                  <Checkbox
                    checked={eventsVisibleInList}
                    onChange={(checked) => setEventsVisibleInList(checked)}
                    color="brandgreen1"
                  />
                  <Label className="tw:!text-sm">Events</Label>
                </CheckboxField>
              )}
            </div>
            <Button
              color="brand-green-outline"
              small
              onClick={() => setSortDirection(sortDirection === 'newest' ? 'oldest' : 'newest')}
              data-testid="activity-log-sort"
            >
              {sortDirection === 'newest' ? 'Newest First' : 'Oldest First'}
            </Button>
          </div>
          {/* Activity List */}
          <div>
            {filteredAndSortedActivityList.map((activity) => (
              <div key={activity.id} data-testid="activity-log-item">
                <ActivityItem
                  activity={activity}
                  noteMutations={noteMutations}
                  noteChannel={noteChannel}
                />
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="tw:p-2  tw:text-zinc-600 tw:italic tw:text-center">No activity</div>
      )}
    </>
  );
}
